/*
* Copyright (C) 2019 SADE Innovations Oy - All Rights Reserved
*
* NOTICE: This software is owned by SADE Innovations Oy and licensed under SADE Booster license.
* All dissemination, usage, modification, copying, reproduction, selling and distribution of the
* software and its intellectual and technical concepts are strictly forbidden without a valid license.
* Such license can be obtained by issuing a SADE Booster License agreement from SADE Innovations Oy
* (https://sadeinnovations.com).
*/

import { Service } from "../backend/AppSyncClientProvider";
import { OtaUpdate, OtaUpdateState } from "./OtaTypes";
import { OtaUpdateListener } from "./OtaUpdateListener";
import {
  DeviceGroupsOtaUpdatesCancelDocument,
  DeviceGroupsOtaUpdatesStartDocument,
  DevicesOtaUpdatesCancelDocument,
  DevicesOtaUpdatesStartDocument,
  OtaUpdatesListDocument,
  OtaUpdatesStatesListDocument,
  OtaUpdatesStatesUpdateFeedDocument,
  OtaUpdateStateFieldsFragment,
} from "../../generated/gqlDevice";
import AppSyncClientFactory from "../backend/AppSyncClientFactory";
import AuthWrapper from "../auth/AuthWrapper";
import { Nullable } from "../../types/aliases";

export default class OtaManager {

  private static instance: OtaManager;

  private otaUpdates?: OtaUpdate[];
  private otaUpdateStates?: OtaUpdateState[];
  private subscriptions: ZenObservable.Subscription[] = [];
  private listeners: OtaUpdateListener[] = [];

  public static getInstance(): OtaManager {
    if (!OtaManager.instance) {
      OtaManager.instance = new OtaManager();
    }
    return this.instance;
  }

  public async init(): Promise<void> {
    if (this.subscriptions.length === 0) {
      console.log("Initializing OTA manager");
      await this.subscribe();
      console.log("OTA manager initialized");
    }
  }

  public uninit(): void {
    this.subscriptions.forEach((s: ZenObservable.Subscription) => {
      s.unsubscribe();
    });
    console.log("OTA manager uninitialized");
  }

  public async getOtaUpdates(forceRefresh?: boolean): Promise<OtaUpdate[]> {
    if (!this.otaUpdates || forceRefresh) {
      return await this.fetchOtaUpdates();
    } else {
      return this.otaUpdates;
    }
  }

  public async getOtaUpdateStates(forceRefresh?: boolean): Promise<OtaUpdateState[]> {
    if (!this.otaUpdateStates || forceRefresh) {
      return await this.fetchOtaUpdateStates();
    } else {
      return this.otaUpdateStates;
    }
  }

  public async triggerDeviceOtaUpdate(deviceId: string, otaId: string): Promise<void> {
    console.log(`triggerDeviceOtaUpdate ${deviceId}, ${otaId}`);

    try {
      const client = AppSyncClientFactory.createProvider().getTypedClient(Service.DEVICE);
      await client.mutate(DevicesOtaUpdatesStartDocument, { deviceId, otaId });
    } catch (error) {
      console.error("Error", error);
    }
  }

  public async triggerGroupOtaUpdate(groupId: string, otaId: string): Promise<void> {
    console.log(`triggerGroupOtaUpdate ${groupId}, ${otaId}`);

    try {
      const client = AppSyncClientFactory.createProvider().getTypedClient(Service.DEVICE);
      await client.mutate(DeviceGroupsOtaUpdatesStartDocument, { groupId, otaId });
    } catch (error) {
      console.error("Error", error);
    }
  }

  public async cancelDeviceOtaUpdate(deviceId: string): Promise<void> {
    console.log(`cancelDeviceOtaUpdate ${deviceId}`);

    try {
      const client = AppSyncClientFactory.createProvider().getTypedClient(Service.DEVICE);
      await client.mutate(DevicesOtaUpdatesCancelDocument, { deviceId });
    } catch (error) {
      console.error("Error", error);
    }
  }

  public async cancelGroupOtaUpdate(groupId: string): Promise<void> {
    console.log(`cancelGroupOtaUpdate ${groupId}`);

    try {
      const client = AppSyncClientFactory.createProvider().getTypedClient(Service.DEVICE);
      await client.mutate(DeviceGroupsOtaUpdatesCancelDocument, { groupId });
    } catch (error) {
      console.error("Error", error);
    }
  }

  public addListener(listener: OtaUpdateListener): void {
    const listenerIndex = this.listeners.indexOf(listener);

    if (listenerIndex !== -1) {
      this.removeListenerFromIndex(listenerIndex);
    }
    this.listeners.push(listener);
    console.log("Listener added");
  }

  public removeListener(listener: OtaUpdateListener): void {
    const listenerIndex = this.listeners.indexOf(listener);

    if (listenerIndex !== -1) {
      this.removeListenerFromIndex(listenerIndex);
      console.log("Listener removed");
    }
  }

  private async fetchOtaUpdates(): Promise<OtaUpdate[]> {
    let nextToken: Nullable<string> = null;
    let otaUpdates: OtaUpdate[] = [];
    const client = AppSyncClientFactory.createProvider().getTypedClient(Service.DEVICE);

    try {
      do {
        const response = await client.query(OtaUpdatesListDocument, { nextToken });
        // cast is required or response is stuck in cyclic type inference loop
        nextToken = (response.data.otaUpdatesList?.nextToken ?? null) as Nullable<string>;
        otaUpdates = otaUpdates.concat(response.data.otaUpdatesList?.otaUpdates ?? []);
      } while (nextToken);
      this.otaUpdates = otaUpdates;
      return otaUpdates;
    } catch (error) {
      console.error("Error", error);
      return [];
    }
  }

  private async fetchOtaUpdateStates(): Promise<OtaUpdateState[]> {
    let nextToken: Nullable<string> = null;
    let otaUpdateStates: OtaUpdateState[] = [];
    const client = AppSyncClientFactory.createProvider().getTypedClient(Service.DEVICE);

    try {
      do {
        const response = await client.query(OtaUpdatesStatesListDocument, { nextToken });
        // cast is required or response is stuck in cyclic type inference loop
        nextToken = (response.data.otaUpdatesStatesList?.nextToken ?? null) as Nullable<string>;
        const convertedStates = response.data.otaUpdatesStatesList?.otaUpdateStates
          .map((fragment) => OtaManager.parseStateFragment(fragment));
        otaUpdateStates = otaUpdateStates.concat(convertedStates ?? []);
      } while (nextToken);
      this.otaUpdateStates = otaUpdateStates;
      return otaUpdateStates;
    } catch (error) {
      console.error("Error", error);
      return [];
    }
  }

  private removeListenerFromIndex(index: number): void {
    this.listeners.splice(index, 1);
  }

  private async subscribe(): Promise<void> {
    (await AuthWrapper.getCurrentAuthenticatedUserClaims())?.canSee.forEach((canSee: string) => {
      this.subscriptions.push(this.subscribeWithIdentity(canSee));
    });
  }

  private subscribeWithIdentity(identity: string): ZenObservable.Subscription {
    const appSyncClient = AppSyncClientFactory.createProvider().getTypedClient(Service.DEVICE);
    return appSyncClient.subscribe(OtaUpdatesStatesUpdateFeedDocument, { receiver: identity })
      .subscribe({
        // TODO: Fix any type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: (error: any): void => {
          if (error.errorMessage === "AMQJS0008I Socket closed.") {
            console.log("Reconnecting socket");
            this.subscribe();
          }
          console.error(error);
        },
        next: (update): void => {
          if (update.data?.otaUpdatesStatesUpdateFeed) {
            const state: OtaUpdateState = OtaManager.parseStateFragment(update.data.otaUpdatesStatesUpdateFeed.item);
            console.log("OTA state: " + JSON.stringify(state));
            this.listeners.forEach((listener: OtaUpdateListener) => {
              listener.onOtaUpdateState(state);
            });
          }
        },
      });
  }

  private static parseStateFragment(fragment: OtaUpdateStateFieldsFragment): OtaUpdateState {
    const { timestamp, ...rest } = fragment;
    return {
      timestamp: typeof timestamp === "string" ? Number.parseInt(timestamp) : timestamp,
      ...rest,
    };
  }
}
